import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { LeadType } from '../../../shared/constants/Tracking.constants';

import { FORM_TRACKING } from '../../../utils/deq/deq.constants';
import { formTrackPageView } from '../../../utils/deq/formTrackPageView';
import { ReactMarkdownExtended } from '../../../utils/generic';
import { calculateExpression } from '../../../utils/generic/mathUtils';

import colors from '../../../styles/colors';
import {
  H1SemiBoldCss,
  H3,
  Label,
  LabelWrapper,
} from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import InfoPopover from '../info-popover/InfoPopover';

import {
  getParsedCalculationOutcome,
  parseCalculationByInputValues,
} from './FormResult.helpers';
import { FormResultProps, InitialSum } from './FormResult.types';

const FormResult = ({
  label,
  placeholder = '',
  calculation,
  contextType,
  inputValues,
  tooltip,
  valueId,
  onCalculate,
  trackingData,
  theme = 'default',
}: FormResultProps) => {
  const isMainResultRef = useRef<HTMLSpanElement>(null);
  const isInputCalculated = useRef(false);

  // Perform DEQ tracking once, when the first calculation is done.
  const handleFormStepTracking = () => {
    if (!isInputCalculated.current) {
      isInputCalculated.current = true;
      formTrackPageView(
        '',
        trackingData?.id || '',
        `calculator - ${trackingData?.trackingName}`.toLowerCase(),
        'end',
        FORM_TRACKING.stepEnd,
        FORM_TRACKING.version,
        LeadType.LEAD_SOFT,
        undefined,
        FORM_TRACKING.stepName,
        FORM_TRACKING.endSuccess
      );
    }
  };

  const parsedCalculation = parseCalculationByInputValues(
    calculation,
    inputValues
  );

  const calculationOutcome = parsedCalculation
    ? calculateExpression(parsedCalculation)
    : '';

  const formattedOutcome = getParsedCalculationOutcome(
    placeholder,
    calculationOutcome,
    contextType
  );

  useEffect(() => {
    if (calculationOutcome && onCalculate) {
      onCalculate(valueId, calculationOutcome);
    }
  }, [calculationOutcome]);

  useEffect(() => {
    if (
      theme === 'large' &&
      formattedOutcome &&
      isMainResultRef.current &&
      isMainResultRef.current.innerText !== InitialSum.ZERO
    ) {
      handleFormStepTracking();
    }
  }, [formattedOutcome]);

  return (
    <div>
      {theme === 'default' && (
        <>
          <LabelWrapper>
            <Label>{label}</Label>
            {tooltip && (
              <InfoPopover label={label}>
                <ReactMarkdownExtended>
                  {tooltip.childMarkdownRemark?.rawMarkdownBody}
                </ReactMarkdownExtended>
              </InfoPopover>
            )}
          </LabelWrapper>
          <OutcomeDefault>{formattedOutcome}</OutcomeDefault>
        </>
      )}
      {theme === 'large' && (
        <>
          <LabelLarge>{label}</LabelLarge>
          <OutcomeLarge ref={isMainResultRef}>{formattedOutcome}</OutcomeLarge>
        </>
      )}
    </div>
  );
};

const LabelLarge = styled(H3)`
  color: ${({ theme }) => theme.colors.abnNeutral50};
  margin: ${Spacings.XS} 0;
`;

const OutcomeDefault = styled.span`
  display: block;
  line-height: 24px;
  margin: ${Spacings.XXS} 0 0;
`;

const OutcomeLarge = styled.span`
  color: ${({ theme }) => theme.colors.abnNeutral50};
  display: block;
  ${H1SemiBoldCss};
  margin: 0;
  word-break: break-all;
`;

export default FormResult;
